import { useMemo } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { produce } from 'immer'

import type {
  PaginatedPost,
  Post,
  PostReactionDetail,
  Scalars,
} from '@tribeplatform/gql-client/types'

import type { QueryClient } from '../../lib/react-query/QueryClient.js'
import type { QueryFilters } from '../../lib/react-query/QueryFilters.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { getFeedKey } from '../../utils/keys/feed.keys.js'
import { getPostKey, getPostsKey } from '../../utils/keys/post.key.js'
import { getPinnedPostsKey } from '../../utils/keys/space.key.js'

export const getCachedRootPostByReplies = (
  id,
  queryClient: QueryClient,
): Post | undefined => {
  if (!id) {
    return undefined
  }
  const filter: QueryFilters = {
    type: 'active',
    queryKey: getPostKey(),
  }
  const posts = queryClient.getQueriesData<Post>(filter)
  let cachedPost: Post
  posts.forEach(([_, queryData]) => {
    if (cachedPost) {
      return
    }
    const node = queryData.replies.nodes?.find(n => n.id === id)
    if (node) {
      cachedPost = queryData
      return
    }
    const edge = queryData.replies?.edges?.map(e => e?.node?.id === id)
    if (edge) {
      cachedPost = queryData
    }
  })

  return cachedPost
}

export const getCachedRootPost = (
  id,
  queryClient: QueryClient,
): Post | undefined => {
  const post = getCachedPost(id, queryClient)
  if (post?.repliedToId) {
    return getCachedRootPost(post.repliedToId, queryClient)
  }
  if (post?.repliedToId === null) {
    return post
  }

  return undefined
}

export const getCachedPost = (
  id: string,
  queryClient: QueryClient,
): Post | undefined => {
  if (!id) {
    return undefined
  }

  let cachedPost: Post

  // get cached post
  const postQueries = queryClient.getQueriesData<Post>(
    getPostKey({ variables: { id } }),
  )
  postQueries?.forEach(query => {
    cachedPost = cachedPost ?? query?.[1]
  })

  // get all posts and feed queries
  const postsQueryFilter: QueryFilters = {
    predicate: query => {
      const queryMainKey = query?.queryKey?.[0]
      if (
        queryMainKey === getFeedKey()[0] ||
        queryMainKey === getPostsKey()[0] ||
        queryMainKey === getPinnedPostsKey()[0]
      ) {
        return true
      }
      return false
    },
  }

  const data =
    queryClient.getQueriesData<InfiniteData<PaginatedPost>>(postsQueryFilter)

  data.forEach(([_, queryData]) => {
    if (Array.isArray(queryData)) {
      queryData.forEach(post => {
        if (post?.id === id) {
          cachedPost = { ...post, ...cachedPost }
        }

        if (post?.replies && !cachedPost) {
          post?.replies?.nodes?.forEach(postReply => {
            if (postReply.id === id) {
              cachedPost = { ...postReply, ...cachedPost }
            }
          })
        }
      })

      // if queryData is paginated list
    } else if (!cachedPost) {
      queryData?.pages?.forEach(page => {
        page?.nodes?.forEach(post => {
          if (post?.id === id) {
            cachedPost = { ...post, ...cachedPost }
          }

          if (post?.replies && !cachedPost) {
            post?.replies?.nodes?.forEach(postReply => {
              if (postReply.id === id) {
                cachedPost = { ...postReply, ...cachedPost }
              }
            })
          }
        })
      })
    }
  })

  return cachedPost && Object.keys(cachedPost).length ? cachedPost : null
}

export const useCachedPost = (id: Scalars['ID']): Post | undefined => {
  const queryClient = useQueryClient()
  return useMemo(() => getCachedPost(id, queryClient), [id, queryClient])
}

export const addPostToPaginatedPost =
  (newPost: Post) => (oldPosts: InfiniteData<PaginatedPost>) => {
    return produce(oldPosts, draft => {
      if (!draft?.pages) {
        draft = {
          pageParams: [],
          pages: [
            {
              pageInfo: {
                hasNextPage: false,
              },
              nodes: [],
              edges: [],
              totalCount: 0,
            },
          ],
        }
      }
      draft.pages[0]?.nodes?.unshift(newPost)
      draft.pages[0].totalCount += 1

      return draft
    })
  }

export const postRepliesUpdater = (node: Post) => (pn: Post) => ({
  ...pn,
  repliesCount: pn?.repliesCount + 1,
  totalRepliesCount: pn?.totalRepliesCount + 1,
  replies: {
    ...pn?.replies,
    totalCount: (pn?.replies?.totalCount || 0) + 1,
    nodes: [node, ...(pn?.replies?.nodes || [])],
    edges: [{ node, cursor: '' }, ...(pn?.replies?.edges || [])],
  },
})

export const infinitePostUpdater =
  (postId: string, updater: (oldPost: Post) => Post, checkReplies = false) =>
  (oldFeed: InfiniteData<PaginatedPost>) => ({
    ...oldFeed,
    pages: oldFeed?.pages?.map(
      infinitePaginatedPostWithRepliesUpdater(postId, updater, checkReplies),
    ),
  })

export const infinitePaginatedPostWithRepliesUpdater =
  (postId: string, updater: (oldPost: Post) => Post, checkReplies = false) =>
  (posts: PaginatedPost): PaginatedPost => {
    return {
      ...posts,
      nodes: posts.nodes?.map(
        infinitePostArrayUpdater(postId, updater, checkReplies),
      ),
      edges: posts.edges?.map(e => {
        let newNode = e.node
        if (e.node.id === postId) {
          newNode = updater(e.node)
        }
        return {
          ...e,
          node: {
            ...newNode,
            ...(checkReplies && {
              replies:
                e.node.replies &&
                infinitePaginatedPostWithRepliesUpdater(
                  postId,
                  updater,
                )(e.node.replies),
            }),
          },
        }
      }),
    }
  }

export const infinitePostArrayUpdater =
  (postId: string, updater: (oldPost: Post) => Post, checkReplies = false) =>
  (post: Post): Post => {
    let newNode = post
    if (post.id === postId) {
      newNode = updater(post)
    }
    return {
      ...newNode,
      ...(checkReplies && {
        replies:
          post.replies &&
          infinitePaginatedPostWithRepliesUpdater(
            postId,
            updater,
          )(post.replies),
      }),
    }
  }

export const removeReactionFromPost =
  (reaction: string, memberId: string) =>
  (old: Post): Post => {
    if (!old?.reactions) {
      return old
    }

    const newReactions: PostReactionDetail[] = old.reactions.reduce(
      (acc, currReaction) => {
        if (currReaction.reaction === reaction) {
          const modifiedReaction: PostReactionDetail = {
            ...currReaction,
            reacted: false,
            count: currReaction.count - 1,
            participants: {
              ...currReaction.participants,
              totalCount: currReaction.participants?.totalCount - 1,
              nodes: currReaction.participants?.nodes?.filter(
                node => node.participant?.id !== memberId,
              ),
              edges: currReaction.participants?.edges?.filter(
                ({ node }) => node.participant?.id !== memberId,
              ),
            },
          }

          if (modifiedReaction.count > 0) {
            acc.push(modifiedReaction)
          }
        } else {
          acc.push(currReaction)
        }
        return acc
      },
      [],
    )

    return {
      ...old,
      reactions: newReactions,
      reactionsCount: old.reactionsCount - 1,
    }
  }

export const addReactionToPost =
  (reaction, userReaction, removeRest = false) =>
  (old: Post): Post => {
    if (!old?.reactions) {
      return old
    }

    const currentReactionIndex = old.reactions.findIndex(
      r => r.reaction === reaction,
    )
    let newReaction: PostReactionDetail = {
      participants: {
        edges: [],
        nodes: [],
        pageInfo: {
          endCursor: '',
          hasNextPage: false,
        },
        totalCount: 0,
      },
      reacted: true,
      count: 0,
      reaction,
    }
    if (currentReactionIndex > -1) {
      newReaction = { ...old.reactions[currentReactionIndex] }
    }
    newReaction.reacted = true
    newReaction.count += 1

    if (!newReaction?.participants) {
      newReaction.participants = {
        totalCount: 0,
        nodes: [],
        edges: [],
        pageInfo: {
          hasNextPage: false,
        },
      }
    }
    newReaction.participants.totalCount += 1
    newReaction.participants?.nodes?.push(userReaction)
    newReaction.participants?.edges?.push({
      node: userReaction,
      cursor: '',
    })
    const newReactions: PostReactionDetail[] =
      currentReactionIndex > -1
        ? old.reactions.map(r => (r.reaction === reaction ? newReaction : r))
        : [newReaction, ...old.reactions]
    const prevReactedCount = old.reactions.filter(
      reaction => reaction.reacted,
    ).length
    const newReactionsCountIfSingle = old.reactionsCount - prevReactedCount + 1
    const newReactionsCountIfMultiple = old.reactionsCount + 1
    const newReactionsCount = removeRest
      ? newReactionsCountIfSingle
      : newReactionsCountIfMultiple

    const newReactionsIfSingle = newReactions
      .map(r => {
        if (!r.reacted) {
          return r
        }

        return {
          ...r,
          reacted: r.reaction === reaction,
          count: r.reaction === reaction ? r.count : r.count - 1,
        }
      })
      .filter(r => r.count > 0)
    return {
      ...old,
      // We assume that all allowed reactions are also listed in singleChoiceReactions when removeRest is true
      reactions: removeRest ? newReactionsIfSingle : newReactions,
      reactionsCount: newReactionsCount,
    }
  }
